import React, { memo, useMemo, useRef, useEffect, useCallback } from "react";
import { LogRowProps } from "../types";
import { LogTimestamp } from "./LogTimestamp";
import { LogLabels } from "./LogLabels";
import { LogContent } from "./LogContent";
import { getUniqueLabels } from "../utils/labelProcessor";
import "./LogRow.css";

/**
 * Single log row with optional timestamp, labels, and content columns
 * Supports 'c' key to copy log content when hovering
 */
export const LogRow = memo(function LogRow({
  entry,
  index,
  showTimestamp,
  showLabels,
  wrapLines,
  prettifyJSON,
  labelsWidth,
  commonLabels,
  onLabelClick,
}: LogRowProps) {
  const isHoveredRef = useRef(false);
  const rowRef = useRef<HTMLDivElement>(null);

  // Get only unique labels (not in common)
  const uniqueLabels = useMemo(() => {
    return getUniqueLabels(entry, commonLabels);
  }, [entry, commonLabels]);

  // Copy log content to clipboard
  const copyLogContent = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(entry.log);
    } catch (e) {
      console.error("Failed to copy log:", e);
    }
  }, [entry.log]);

  // Handle keyboard shortcut for copying
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "c" && isHoveredRef.current && !e.ctrlKey && !e.metaKey && !e.altKey) {
        // Don't trigger if user is in an input or selecting text
        const target = e.target as HTMLElement;
        const isInInput = target.tagName === "INPUT" || target.tagName === "TEXTAREA";
        const hasSelection = window.getSelection()?.toString();
        if (!isInInput && !hasSelection) {
          e.preventDefault();
          copyLogContent();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [copyLogContent]);

  return (
    <div
      ref={rowRef}
      className="log-row"
      title="Press 'c' to copy"
      onMouseEnter={() => { isHoveredRef.current = true; }}
      onMouseLeave={() => { isHoveredRef.current = false; }}
    >
      {showTimestamp && <LogTimestamp timestamp={entry.timestamp} />}
      {showLabels && <LogLabels labels={uniqueLabels} width={labelsWidth} onLabelClick={onLabelClick} />}
      <LogContent text={entry.log} wrap={wrapLines} prettifyJSON={prettifyJSON} />
    </div>
  );
});
